import React from "react";
import { Box, Typography, Paper, Chip, Skeleton, Pagination } from "@mui/material";
import {
  History as HistoryIcon,
  Notes as NotesIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { formatDateTime, getStatusChipStyle, extractDomains } from "./helpers";

export default function RequestHistoryView({
  loading,
  requestsList,
  totalPages,
  currentPage,
  onPageChange,
}) {
  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {Array.from(new Array(3)).map((_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={110}
            sx={{ borderRadius: "12px" }}
          />
        ))}
      </Box>
    );
  }

  if (!requestsList || requestsList.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: "12px",
          border: "1px dashed #CBD5E1",
          backgroundColor: "#F8FAFC",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}
      >
        <HistoryIcon sx={{ fontSize: 36, color: "#94A3B8" }} />
        <Typography
          sx={{
            fontFamily: '"Poppins", sans-serif',
            fontSize: "0.95rem",
            fontWeight: 600,
            color: "#334155",
          }}
        >
          No Request History
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Poppins", sans-serif',
            fontSize: "0.82rem",
            color: "#64748B",
          }}
        >
          Your institute has not submitted any trainer change requests yet.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {requestsList.map((req, index) => {
        const itemIndex = (currentPage - 1) * 5 + index + 1;
        const statusStyle = getStatusChipStyle(req.status);
        const { date: reqDate, time: reqTime } = formatDateTime(req.created_at);
        const { date: actDate, time: actTime } = formatDateTime(req.action_date);
        const reqTrainer = req.requested_trainer || {};
        const prevTrainer = req.previous_trainer || null;
        const reqDomainInfo = extractDomains(reqTrainer.domains);
        const prevDomainInfo = prevTrainer ? extractDomains(prevTrainer.domains) : null;

        return (
          <Paper
            key={req.request_id || req.id || index}
            elevation={0}
            sx={{
              p: 2.25,
              borderRadius: "12px",
              border: "1px solid #E2E8F0",
              backgroundColor: "#FFFFFF",
              display: "flex",
              flexDirection: "column",
              gap: 1.75,
              transition: "all 0.15s ease",
              "&:hover": {
                borderColor: "#CBD5E1",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              },
            }}
          >
            {/* Top Row: Request Index, Date & Status Chip */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarIcon sx={{ fontSize: 16, color: "#64748B" }} />
                <Typography
                  sx={{
                    fontFamily: '"Poppins", sans-serif',
                    fontWeight: 600,
                    fontSize: "13px",
                    color: "#1E293B",
                  }}
                >
                  {reqDate} {reqTime ? `at ${reqTime}` : ""}
                </Typography>
              </Box>

              <Chip
                icon={statusStyle.icon}
                label={statusStyle.label}
                size="small"
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  backgroundColor: statusStyle.bg,
                  color: statusStyle.color,
                  border: statusStyle.border,
                  fontSize: "11.5px",
                  fontWeight: 600,
                  height: "24px",
                  borderRadius: "12px",
                  "& .MuiChip-icon": { ml: 0.5 },
                }}
              />
            </Box>

            {/* Trainers Details: Previous Trainer & Requested Trainer */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: prevTrainer
                  ? { xs: "1fr", sm: "1fr 1fr" }
                  : "1fr",
                gap: 1.5,
              }}
            >
              {/* Previous Trainer Card (if available) */}
              {prevTrainer && (
                <Box
                  sx={{
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    borderRadius: "10px",
                    p: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Poppins", sans-serif',
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "#64748B",
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                      }}
                    >
                      Previously Assigned
                    </Typography>
                    <Chip
                      label="Previous"
                      size="small"
                      sx={{
                        fontFamily: '"Poppins", sans-serif',
                        backgroundColor: "#F1F5F9",
                        color: "#475569",
                        fontSize: "10px",
                        fontWeight: 600,
                        height: "18px",
                      }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      fontFamily: '"Poppins", sans-serif',
                      fontSize: "13.5px",
                      fontWeight: 700,
                      color: "#334155",
                    }}
                  >
                    {prevTrainer.trainer_name || "—"}
                  </Typography>

                  {/* Previous Domains */}
                  {prevDomainInfo && prevDomainInfo.all.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, pt: 0.25 }}>
                      {prevDomainInfo.fdp.map((d, di) => (
                        <Chip
                          key={`p-fdp-${di}`}
                          label={`FDP: ${d}`}
                          size="small"
                          sx={{
                            fontFamily: '"Poppins", sans-serif',
                            backgroundColor: "#F1F5F9",
                            color: "#475569",
                            border: "1px solid #E2E8F0",
                            fontSize: "10.5px",
                            fontWeight: 500,
                            height: "20px",
                            borderRadius: "4px",
                          }}
                        />
                      ))}
                      {prevDomainInfo.techcamp.map((d, di) => (
                        <Chip
                          key={`p-tc-${di}`}
                          label={`TechCamp: ${d}`}
                          size="small"
                          sx={{
                            fontFamily: '"Poppins", sans-serif',
                            backgroundColor: "#F1F5F9",
                            color: "#475569",
                            border: "1px solid #E2E8F0",
                            fontSize: "10.5px",
                            fontWeight: 500,
                            height: "20px",
                            borderRadius: "4px",
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Requested Trainer Card */}
              <Box
                sx={{
                  backgroundColor: prevTrainer ? "#EFF6FF" : "#F8FAFC",
                  border: prevTrainer ? "1px solid #BFDBFE" : "1px solid #F1F5F9",
                  borderRadius: "10px",
                  p: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"Poppins", sans-serif',
                      fontSize: "11px",
                      fontWeight: 600,
                      color: prevTrainer ? "#2563EB" : "#64748B",
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                    }}
                  >
                    Requested Trainer
                  </Typography>
                  <Chip
                    label="Requested"
                    size="small"
                    sx={{
                      fontFamily: '"Poppins", sans-serif',
                      backgroundColor: prevTrainer ? "#DBEAFE" : "#EFF6FF",
                      color: "#1E40AF",
                      fontSize: "10px",
                      fontWeight: 600,
                      height: "18px",
                    }}
                  />
                </Box>

                <Typography
                  sx={{
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: "13.5px",
                    fontWeight: 700,
                    color: "#0F172A",
                  }}
                >
                  {reqTrainer.trainer_name || "—"}
                </Typography>

                {/* Requested Domains */}
                {reqDomainInfo.all.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, pt: 0.25 }}>
                    {reqDomainInfo.fdp.map((d, di) => (
                      <Chip
                        key={`fdp-${di}`}
                        label={`FDP: ${d}`}
                        size="small"
                        sx={{
                          fontFamily: '"Poppins", sans-serif',
                          backgroundColor: "#EFF6FF",
                          color: "#1E40AF",
                          border: "1px solid #DBEAFE",
                          fontSize: "10.5px",
                          fontWeight: 500,
                          height: "20px",
                          borderRadius: "4px",
                        }}
                      />
                    ))}
                    {reqDomainInfo.techcamp.map((d, di) => (
                      <Chip
                        key={`tc-${di}`}
                        label={`TechCamp: ${d}`}
                        size="small"
                        sx={{
                          fontFamily: '"Poppins", sans-serif',
                          backgroundColor: "#FAF5FF",
                          color: "#6B21A8",
                          border: "1px solid #F3E8FF",
                          fontSize: "10.5px",
                          fontWeight: 500,
                          height: "20px",
                          borderRadius: "4px",
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>

            {/* SPOC Remark if any */}
            {req.remark && (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <NotesIcon sx={{ fontSize: 16, color: "#64748B", mt: 0.2 }} />
                <Typography
                  sx={{
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: "12.5px",
                    color: "#475569",
                    fontStyle: "italic",
                  }}
                >
                  "{req.remark}"
                </Typography>
              </Box>
            )}

            {/* Admin Reason / Notes if rejected or approved with reason */}
            {req.reason && (
              <Box
                sx={{
                  backgroundColor:
                    String(req.status).toLowerCase() === "rejected"
                      ? "#FEF2F2"
                      : "#F0FDF4",
                  border: `1px solid ${String(req.status).toLowerCase() === "rejected"
                      ? "#FCA5A5"
                      : "#BBF7D0"
                    }`,
                  borderRadius: "8px",
                  p: 1.25,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: "12px",
                    fontWeight: 700,
                    color:
                      String(req.status).toLowerCase() === "rejected"
                        ? "#991B1B"
                        : "#166534",
                    mb: 0.25,
                  }}
                >
                  Coordinator Reason:
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: "12.5px",
                    color:
                      String(req.status).toLowerCase() === "rejected"
                        ? "#7F1D1D"
                        : "#14532D",
                  }}
                >
                  {req.reason}
                </Typography>
              </Box>
            )}
          </Paper>
        );
      })}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, val) => onPageChange(val)}
            size="small"
            shape="rounded"
          />
        </Box>
      )}
    </Box>
  );
}
